<template>
  <div class="mh-v login-page">
    <header class="main-header">
      <div class="container">
        <div class="d-flex h-110">
          <div class="logo">
            <a href="https://www.21grams.se/">
              <img src="@/assets/img/logo.svg" alt="" />
            </a>
          </div>
          <div class="header-title align-self-center">
            <img src="@/assets/img/21grams-white-logo.svg" alt="" />
          </div>
        </div>
      </div>
    </header>

    <div class="main-full-height">
      <div class="container">
        <div class="wrap-form">
          <b-alert :model-value="isCredentialsError" variant="danger">Invalid credentials</b-alert>
          <div>
            <VeeForm @submit="login">
              <VInput v-model="authData.email" type="text" name="Email" rules="emailPhoneRequired" @input="onInput" />
              <VInput
                v-model="authData.password"
                class="mb-0"
                type="password"
                name="Password"
                rules="required"
                @input="onInput"
              />
              <b-button class="w-100" type="submit" :disabled="isDisabled"> Login </b-button>
              <b-form-group class="mb-3 mt-4 text-white">
                <input
                  id="keepLoggedIn"
                  v-model="authData.keepLoggedIn"
                  class="form-check-input"
                  type="checkbox"
                  :value="true"
                />
                <label for="keepLoggedIn">Keep me logged in</label>
              </b-form-group>
            </VeeForm>
            <div class="d-flex mt-4 justify-content-between">
              <router-link :to="registerLink" class="link"> Don't have an account? Request access </router-link>

              <router-link :to="forgotPasswordLink" class="link"> Forgot Password? </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="pattern" />

    <LayoutFooter />
  </div>
</template>

<script lang="ts">
import type { RouterLink } from '@/types/Components/Router'
import type { AuthData } from '@/types/Models/Account'
import { BAlert, BButton, BFormGroup } from 'bootstrap-vue-next'
import { Form as VeeForm } from 'vee-validate'
import { ref, watch, defineComponent, computed, type Ref, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { none } from '@/services/Helpers'
import { useAccountStore } from '@/store/account'
import { useAuthStore } from '@/store/auth'
import { CUSTOMER_FORGOT_PASSWORD, REGISTRATION } from '@/types/router'
import VInput from '@/views/Components/Elements/Form/VInput.vue'
import LayoutFooter from '@/views/Layouts/LayoutFooter.vue'

export default defineComponent({
  name: 'LoginIndex',
  components: {
    BFormGroup,
    BButton,
    BAlert,
    VeeForm,
    LayoutFooter,
    VInput,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    const accountStore = useAccountStore()

    const isCredentialsError: Ref<boolean> = ref(false)
    const hasChanged: Ref<boolean> = ref(false)
    const authData: Ref<AuthData> = ref({
      email: null,
      password: null,
      keepLoggedIn: true,
    })

    const registerLink: RouterLink = { name: REGISTRATION }
    const forgotPasswordLink: RouterLink = { name: CUSTOMER_FORGOT_PASSWORD }

    const isAuthenticated: ComputedRef<boolean> = computed(() => authStore.authenticated)
    const userId: ComputedRef<string | null> = computed(() => accountStore.userId)
    const isDisabled: ComputedRef<boolean> = computed(
      () => hasChanged.value && (!authData.value.email || !authData.value.password),
    )

    const login = () => {
      authStore.login(authData.value).catch(() => {
        isCredentialsError.value = true
      })
    }

    const onInput = () => {
      hasChanged.value = true
    }

    watch(
      [isAuthenticated, userId],
      () => {
        if (!isAuthenticated.value || !userId.value) return
        const redirectQuery = route.query.redirect
        const redirectPath = Array.isArray(redirectQuery) ? redirectQuery[0] : redirectQuery

        if (redirectPath && redirectPath !== '/') {
          router.push(redirectPath).catch(none)
          return
        }

        router.push({ name: accountStore.homePage }).catch(none)
      },
      { immediate: true, deep: true },
    )

    return {
      authData,
      isCredentialsError,
      forgotPasswordLink,
      registerLink,
      isDisabled,
      onInput,
      login,
    }
  },
})
</script>

<style scoped>
.link {
  color: #ffffff;
}
.form-check-input {
  border-color: #fff;
  margin: 0 10px 0 0;
}
</style>
